import { FC, ReactNode, useMemo } from "react";
import { Channel, MessageInput, MessageList } from "stream-chat-react";
import styled from "@emotion/styled";

import { useChatService, useConnectChannel } from "@/services/ChatServiceNew";
import Button from "@/components/input/Button";
import Icon from "@/components/misc/Icon";
import { SITE_PATHS } from "@/config/routing";
import Avatar from "@/components/misc/Avatar";
import { ContractId } from "@/services/ContractsService";
import { ChatRoomLoading } from "@/features/chat/ChatRoom";
import { screenSmallerThan } from "@/styles";

import "stream-chat-react/dist/css/v2/index.css";

import {
  StyledHeader,
  StyledRoomTitle,
  StyledRoomTitleSecondary,
  StyledTopLeftContent,
  StyledTopSection,
  StyledTopSectionContent,
} from "./ChatRoom.styles";

const StyledContainer = styled.div`
  height: 100%;
  width: 100%;

  .str-chat__container {
    display: flex;
    flex-direction: column;
  }

  ${screenSmallerThan.tablet} {
    .str-chat__message-input {
      padding-block: 0.75rem;
    }
  }
`;

export type UserId = string;

export type User = {
  id: UserId;
  name: string;
  image?: string;
  email?: string;
};

interface IChatRoomProps {
  contractId: ContractId;
  currentUserId: UserId;
  users: User[];
  menuButton?: ReactNode;
  className?: string;
  showBackButton?: boolean;
  header?: {
    appendContent?: ReactNode;
    appendBelowContent?: ReactNode;
  };
  hideInputs?: boolean;
  footer?: {
    appendContent?: React.ReactNode;
  };
}

const ChatRoom: FC<IChatRoomProps> = ({
  contractId,
  className,
  showBackButton,
  currentUserId,
  users,
  header = {},
  hideInputs,
  footer,
}) => {
  const { streamClient } = useChatService();
  const { channel } = useConnectChannel({
    contractId,
  });

  const usersExceptCurrent = useMemo(
    () => users.filter((user) => user.id !== currentUserId),
    [users, currentUserId]
  );
  const displayUserDetails = usersExceptCurrent[0];

  if (!streamClient || !channel) {
    return <ChatRoomLoading />;
  }

  return (
    <StyledContainer className={className}>
      <Channel
        channel={channel}
        LoadingIndicator={() => <ChatRoomLoading />}
      >
        <StyledHeader>
          <StyledTopSection>
            <StyledTopLeftContent>
              {showBackButton && (
                <Button variant="ghost" link={SITE_PATHS.CHAT_PAGE}>
                  <Icon isSrcRelative size="xxs" src="chevron_left.svg" />
                </Button>
              )}
              <Avatar
                img={displayUserDetails?.image}
                size="lg"
                firstName={displayUserDetails.name}
              />
            </StyledTopLeftContent>

            <StyledTopSectionContent>
              <StyledRoomTitle>{displayUserDetails?.name}</StyledRoomTitle>
              <StyledRoomTitleSecondary>
                {displayUserDetails?.email}
              </StyledRoomTitleSecondary>
            </StyledTopSectionContent>

            {header?.appendContent || null}
          </StyledTopSection>

          {header?.appendBelowContent || null}
        </StyledHeader>

        <MessageList />

        {!hideInputs && <MessageInput grow maxRows={5} />}

        {footer?.appendContent || null}
      </Channel>
    </StyledContainer>
  );
};

export default ChatRoom;
